import React from 'react';
import { formatCOP, formatNumber } from '../utils/kpiFormatters';
import type { KpiTopProductItem } from '../types';

interface Props {
  products: KpiTopProductItem[];
}

export const TopProductsTable: React.FC<Props> = ({ products }) => {
  if (!products || products.length === 0) {
    return (
      <div className="py-12 text-center text-xs text-text-muted">
        No hay ventas de productos registradas en este periodo
      </div>
    );
  }

  const maxRevenue = Math.max(...products.map((p) => p.total_revenue), 1);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="border-b border-border text-text-muted text-[11px] font-bold uppercase tracking-wider">
            <th className="py-2.5 px-3">#</th>
            <th className="py-2.5 px-3">Producto</th>
            <th className="py-2.5 px-3 text-right">Cant.</th>
            <th className="py-2.5 px-3 text-right">Facturación</th>
            <th className="py-2.5 px-3 text-right">Margen</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/50">
          {products.map((p, idx) => {
            const barWidth = Math.min(100, Math.round((p.total_revenue / maxRevenue) * 100));

            return (
              <tr key={p.id} className="hover:bg-muted/40 transition">
                {/* Ranking badge */}
                <td className="py-3 px-3 font-bold text-text-muted">
                  {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                </td>

                {/* Nombre y categoría con barra de progreso de fondo */}
                <td className="py-3 px-3">
                  <div className="font-bold text-text">{p.name}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-text-muted font-medium bg-muted px-1.5 py-0.5 rounded">
                      {p.category}
                    </span>
                    <div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden hidden sm:block">
                      <div
                        className="h-full bg-emerald-500 rounded-full"
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                  </div>
                </td>

                {/* Cantidad vendida */}
                <td className="py-3 px-3 text-right font-semibold text-text">
                  {formatNumber(p.total_qty)}
                </td>

                {/* Facturación */}
                <td className="py-3 px-3 text-right font-black text-text">
                  {formatCOP(p.total_revenue)}
                </td>

                {/* Margen % */}
                <td className="py-3 px-3 text-right">
                  <span
                    className={`inline-block px-2 py-0.5 rounded-md font-bold text-[11px] ${
                      p.margin_pct >= 60
                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300'
                        : p.margin_pct >= 40
                        ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300'
                        : 'bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300'
                    }`}
                  >
                    {p.margin_pct.toFixed(1)}%
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
